import type { Chat } from "@ai-sdk/react";
import { useHotkeys } from "@mantine/hooks";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api-client";
import { AiChatContext, AiChatWidthContext } from "./ai-chat-context";
import {
  clampAiChatWidth,
  readOrCreateAiChatThreadId,
  readStoredAiChatWidth,
  startNewAiChatThreadId,
  storeAiChatWidth,
} from "./aiChatPanelStorage";
import { useHasAiChatAccess } from "./useHasAiChatAccess";
import type { LimeAiUIMessage } from "./types";

// The AI SDK is only downloaded for users who can open the chat.
function loadCreateAiChat() {
  return import("./createAiChat").then((module) => module.createAiChat);
}

function viewportWidth() {
  return typeof window === "undefined" ? 1440 : window.innerWidth;
}

export function AiChatProvider({ children }: { children: React.ReactNode }) {
  const hasAccess = useHasAiChatAccess();
  // Stable across reloads, so the server keeps appending to one thread and can
  // hand the transcript back on the next load. Only `startNewChat` replaces it.
  const [threadId, setThreadId] = useState(readOrCreateAiChatThreadId);

  // The Chat instance lives here, not inside useChat: useChat keeps it in a ref
  // keyed on `id`, so the conversation would be discarded every time the panel
  // body remounts — which it does on every crossing of the lg breakpoint.
  const [chat, setChat] = useState<Chat<LimeAiUIMessage> | null>(null);

  useEffect(() => {
    if (!hasAccess) return;
    let cancelled = false;
    loadCreateAiChat()
      .then((createAiChat) => {
        if (cancelled) return;
        setChat((current) =>
          current?.id === threadId ? current : createAiChat(threadId),
        );
      })
      .catch((error: unknown) => Sentry.captureException(error));
    return () => {
      cancelled = true;
    };
  }, [hasAccess, threadId]);

  const { data: restored } = api.ai.useGetAiChatThread(
    { id: threadId },
    { enabled: hasAccess },
  );
  const hasHydrated = useRef(false);

  // Restores the transcript once, and only onto an untouched chat: the fetch
  // can land after the user has already started typing or streaming, and
  // overwriting that would discard their turn.
  useEffect(() => {
    if (!chat || hasHydrated.current) return;
    const messages = restored?.thread?.messages;
    if (messages === undefined || messages.length === 0) return;
    if (chat.messages.length > 0) return;

    hasHydrated.current = true;
    chat.messages = messages as LimeAiUIMessage[];
  }, [chat, restored]);

  const [isOpen, setIsOpen] = useState(false);
  const [width, setWidth] = useState(() =>
    readStoredAiChatWidth(viewportWidth()),
  );

  /**
   * A new `Chat` rather than a cleared one: the id is what the server threads
   * on, so reusing the instance would append the fresh conversation to the
   * abandoned transcript.
   */
  const startNewChat = useCallback(() => {
    // The restore request for the abandoned thread can still be in flight, and
    // it must not repopulate the empty one it lands in.
    hasHydrated.current = true;
    const nextId = startNewAiChatThreadId();
    // Swapped in together so the panel never sees a missing chat and unmounts.
    loadCreateAiChat()
      .then((createAiChat) => {
        setThreadId(nextId);
        setChat(createAiChat(nextId));
      })
      .catch((error: unknown) => Sentry.captureException(error));
  }, []);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((value) => !value), []);

  const commitWidth = useCallback((next: number) => {
    const clamped = clampAiChatWidth(next, viewportWidth());
    setWidth(clamped);
    storeAiChatWidth(clamped);
  }, []);

  useHotkeys([["mod+J", toggle]]);

  // Null until the AI SDK has loaded, which is also what users without access see.
  const chatValue = useMemo(
    () => (chat ? { chat, isOpen, open, close, toggle, startNewChat } : null),
    [chat, isOpen, open, close, toggle, startNewChat],
  );
  const widthValue = useMemo(
    () => ({ width, commitWidth }),
    [width, commitWidth],
  );

  return (
    <AiChatContext.Provider value={chatValue}>
      <AiChatWidthContext.Provider value={widthValue}>
        {children}
      </AiChatWidthContext.Provider>
    </AiChatContext.Provider>
  );
}
